/**
 * RRULE Helper Utilities
 *
 * Converts between RRULE strings and human-readable descriptions,
 * and generates RRULE strings from day-of-week selections.
 *
 * SCRUM-821 Chunk C: AC-12, AC-16
 */

const DAY_MAP = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

const DAY_ABBREVS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const WEEKDAY_SET = new Set(['MO', 'TU', 'WE', 'TH', 'FR']);
const WEEKEND_SET = new Set(['SA', 'SU']);

/**
 * Convert an RRULE string to a human-readable description.
 *
 * @param {string} rrule - RRULE string (e.g. "FREQ=WEEKLY;BYDAY=MO,WE,FR")
 * @returns {string} Human-readable description
 */
export function rruleToHumanReadable(rrule) {
  if (!rrule || typeof rrule !== 'string') {
    return 'Invalid pattern';
  }

  // Parse RRULE parts into key-value map
  const parts = {};
  for (const segment of rrule.split(';')) {
    const [key, value] = segment.split('=');
    if (key && value) {
      parts[key.toUpperCase()] = value.toUpperCase();
    }
  }

  const freq = parts.FREQ;
  if (!freq) {
    return 'Invalid pattern';
  }

  // Handle WEEKLY with BYDAY (most common for availability)
  if (freq === 'WEEKLY' && parts.BYDAY) {
    const days = parts.BYDAY.split(',').map(d => d.trim());
    const daySet = new Set(days);

    // Check for common patterns
    if (daySet.size === 5 && [...WEEKDAY_SET].every(d => daySet.has(d))) {
      return 'Every weekday';
    }
    if (daySet.size === 2 && [...WEEKEND_SET].every(d => daySet.has(d))) {
      return 'Every weekend';
    }
    if (daySet.size === 7) {
      return 'Every day';
    }

    // List individual days
    const dayNames = days
      .sort((a, b) => DAY_ABBREVS.indexOf(a) - DAY_ABBREVS.indexOf(b))
      .map(d => DAY_MAP[d] || d);

    if (dayNames.length === 1) {
      return `Every ${dayNames[0]}`;
    }

    return `Every ${dayNames.slice(0, -1).join(', ')} and ${dayNames[dayNames.length - 1]}`;
  }

  // Handle DAILY
  if (freq === 'DAILY') {
    return 'Every day';
  }

  // Handle MONTHLY with BYDAY ordinal (e.g. FREQ=MONTHLY;BYDAY=1MO or BYDAY=-1FR)
  if (freq === 'MONTHLY' && parts.BYDAY) {
    const match = parts.BYDAY.match(/^(-?\d+)([A-Z]{2})$/);
    if (match) {
      const ordinalWords = { '1': 'first', '2': 'second', '3': 'third', '4': 'fourth', '-1': 'last' };
      const word = ordinalWords[match[1]];
      const dayName = DAY_MAP[match[2]];
      if (word && dayName) {
        return `Every ${word} ${dayName} of the month`;
      }
    }
  }

  // Handle MONTHLY with BYMONTHDAY
  if (freq === 'MONTHLY' && parts.BYMONTHDAY) {
    const day = parts.BYMONTHDAY;
    return `Monthly on day ${day}`;
  }

  // Handle YEARLY with BYMONTH and BYMONTHDAY (e.g. FREQ=YEARLY;BYMONTH=5;BYMONTHDAY=15)
  if (freq === 'YEARLY' && parts.BYMONTH && parts.BYMONTHDAY) {
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const month = monthNames[parseInt(parts.BYMONTH, 10)] || parts.BYMONTH;
    return `Every year on ${month} ${parts.BYMONTHDAY}`;
  }

  // Handle YEARLY (plain)
  if (freq === 'YEARLY') {
    return 'Yearly';
  }

  // Fallback for other weekly patterns
  if (freq === 'WEEKLY') {
    return 'Weekly';
  }

  return rrule;
}

/**
 * Generate an RRULE string from selected day-of-week codes.
 *
 * @param {string[]} selectedDays - Array of day codes (e.g. ['MO', 'WE', 'FR'])
 * @returns {string} RRULE string
 */
export function daysToRRule(selectedDays) {
  if (!Array.isArray(selectedDays) || selectedDays.length === 0) {
    return '';
  }

  // Sort by standard day order and filter valid codes
  const valid = selectedDays
    .map(d => d.toUpperCase())
    .filter(d => DAY_ABBREVS.includes(d))
    .sort((a, b) => DAY_ABBREVS.indexOf(a) - DAY_ABBREVS.indexOf(b));

  if (valid.length === 0) {
    return '';
  }

  return `FREQ=WEEKLY;BYDAY=${valid.join(',')}`;
}

/**
 * Generate a monthly ordinal RRULE string.
 * Uses BYDAY=NDA encoding (NOT BYSETPOS).
 *
 * MVP 1.12.4 B-02
 * Story: SCRUM-1144
 *
 * @param {string} dayCode - 'MO'|'TU'|'WE'|'TH'|'FR'|'SA'|'SU'
 * @param {number} position - 1 (first), 2 (second), 3 (third), 4 (fourth), -1 (last)
 * @returns {string} RRULE string, e.g. "FREQ=MONTHLY;BYDAY=1MO"
 */
/**
 * Given a date string (YYYY-MM-DD), returns the ordinal position and day code
 * for use with generateMonthlyOrdinalRRule().
 *
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @returns {{ position: number, dayCode: string }}
 *   position: 1-4 for first–fourth occurrence; -1 for last (5th occurrence)
 *   dayCode:  'MO'|'TU'|'WE'|'TH'|'FR'|'SA'|'SU'
 */
export function getOrdinalFromDate(dateStr) {
  if (!dateStr) {
    return { position: 1, dayCode: 'SU' };
  }

  const d = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = d.getDay(); // 0=Su, 1=Mo, ..., 6=Sa
  const dayCode = DAY_ABBREVS[dayOfWeek];
  const dayOfMonth = d.getDate();

  const occurrence = Math.ceil(dayOfMonth / 7);
  const position = occurrence >= 5 ? -1 : occurrence;

  return { position, dayCode };
}

/**
 * Generate a monthly ordinal RRULE string.
 * Uses BYDAY=NDA encoding (NOT BYSETPOS).
 *
 * MVP 1.12.4 B-02
 * Story: SCRUM-1144
 *
 * @param {string} dayCode - 'MO'|'TU'|'WE'|'TH'|'FR'|'SA'|'SU'
 * @param {number} position - 1 (first), 2 (second), 3 (third), 4 (fourth), -1 (last)
 * @returns {string} RRULE string, e.g. "FREQ=MONTHLY;BYDAY=1MO"
 */
export function generateMonthlyOrdinalRRule(dayCode, position) {
  const validDays = new Set(DAY_ABBREVS);
  const validPositions = new Set([1, 2, 3, 4, -1]);

  if (!validDays.has(dayCode) || !validPositions.has(position)) {
    return '';
  }

  return `FREQ=MONTHLY;BYDAY=${position}${dayCode}`;
}
